import { api } from '@common/api';
import { PrimaryButton } from '@components/atoms';
import { InputMask } from '@components/atoms/InputMask/InputMask';
import { Col, Form, Input, message, Row, Select, Typography } from 'antd';
import { useState } from 'react';
const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

export const ContactPage = () => {
  const [form] = Form.useForm();
  const [showThankyou, setShowThankyou] = useState(false);
  const [lastSubject, setLastSubject] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onFinish = async (values: ContactFormValues) => {
    setIsSubmitting(true);
    try {
      await api.post('/contact', values);
      setLastSubject(values.subject);
      setShowThankyou(true);
      message.success('Your message has been sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      message.error('Unable to send your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    form.resetFields();
    setShowThankyou(false);
  };

  const ThankYouMessage = () => (
    <div className="bg-zinc-900 p-6 text-center">
      <div className="mb-6">
        <img src="/assets/icons/mail-icon.svg" alt="Mail sent" className="mx-auto h-16" />
      </div>
      <Title level={3} className="font-android !font-normal !text-white mb-6">THANK YOU FOR REACHING OUT</Title>
      <Paragraph className="font-barlow text-gray-300 text-lg mb-8">
        Your message has been successfully received. Our team will review your inquiry about <span className="text-yellow-principal">{lastSubject}</span> and respond as soon as possible.
      </Paragraph>
      <Paragraph className="font-barlow text-gray-300 mb-8">
        In the meantime, you might be interested in exploring our services or viewing our project gallery.
      </Paragraph>
      <PrimaryButton
        type="primary"
        onClick={resetForm}
        className="w-full md:w-auto"
      >
        SEND ANOTHER MESSAGE
      </PrimaryButton>
    </div>
  );

  return (
    <div>
      {/* Hero Section */}
      <section className="relative w-full h-[400px]">
        <div className="contact-bg opacity-60 absolute inset-0 w-full h-full"></div>
        <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 md:py-16 lg:py-20 z-10">
          <Title level={1} className="font-android !font-normal text-yellow-principal text-center">
            CONTACT US
          </Title>
          <div className="max-w-3xl mx-auto">
            <Paragraph className="font-barlow text-gray-300 text-center md:text-lg lg:!text-xl">
              Get in touch with our team to discuss your project or to request a quote.
            </Paragraph>
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="w-full bg-second-black-carbon pt-8 pb-18">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">

            <div className="bg-zinc-900 flex flex-col items-center p-6 md:p-8 text-center">
              <div className="mb-4 md:mb-6">
                <img src="/assets/icons/phone-icon.svg" alt="Phone icon" className="h-8 md:h-auto" />
              </div>
              <Title level={3} className="font-android !font-normal !text-white text-lg md:text-xl">CALL US</Title>
              <Paragraph className="font-barlow text-gray-300 md:text-lg !mb-1">
                +1 (530) 587-4509
              </Paragraph>
              <Paragraph className="font-barlow text-gray-300 text-sm">
                Monday - Friday: 7am - 5pm
              </Paragraph>
            </div>

            <div className="bg-zinc-900 flex flex-col items-center p-6 md:p-8 text-center">
              <div className="mb-4 md:mb-6">
                <img src="/assets/icons/mail-icon.svg" alt="Mail icon" />
              </div>
              <Title level={3} className="font-android !font-normal !text-white text-lg md:text-xl">EMAIL US</Title>
              <Paragraph className="font-barlow text-gray-300 md:text-lg !mb-1">
                contact@ruppertinc.com
              </Paragraph>
              <Paragraph className="font-barlow text-gray-300 text-sm">
                We respond within 24h
              </Paragraph>
            </div>

            <div className="bg-zinc-900 flex flex-col items-center p-6 md:p-8 text-center">
              <div className="mb-4 md:mb-6">
                <img src="/assets/icons/location-icon.svg" alt="Location icon" className="h-8 md:h-auto" />
              </div>
              <Title level={3} className="font-android !font-normal !text-white text-lg md:text-xl">VISIT US</Title>
              <Paragraph className="font-barlow text-gray-300 text-sm">
                11168 W River St <br />
                Truckee, CA 96161
              </Paragraph>
            </div>
          </div>
        </div>
      </section>

      {/* Form and Map Section Side by Side */}
      <section className="w-full pt-8 pb-18 bg-second-black-carbon">
        <div className="container mx-auto px-4">
          <Row gutter={[48, 48]}>
            {/* Contact Form Column */}
            <Col xs={24} lg={12}>
              <Title level={2} className="font-android !font-normal text-yellow-principal">SEND US A MESSAGE</Title>
              <div className="bg-zinc-900 p-6">
                {!showThankyou ? (
                  <Form
                    form={form}
                    name="contact-form"
                    layout="vertical"
                    onFinish={onFinish}
                    requiredMark={false}
                    disabled={isSubmitting}
                  >
                    <Form.Item
                      label={<span className="font-android !font-normal text-white">FULL NAME</span>}
                      name="name"
                      rules={[{ required: true, message: 'Please enter your name' }]}
                    >
                      <Input className="bg-zinc-800 font-barlow border-zinc-700 text-white" placeholder="Your full name" />
                    </Form.Item>

                    <Form.Item
                      label={<span className="font-android !font-normal text-white">EMAIL ADDRESS</span>}
                      name="email"
                      rules={[
                        { required: true, message: 'Please enter your email' },
                        { type: 'email', message: 'Please enter a valid email' }
                      ]}
                    >
                      <Input className="bg-zinc-800 font-barlow border-zinc-700 text-white" placeholder="Your email address" />
                    </Form.Item>

                    <Form.Item
                      label={<span className="font-android !font-normal text-white">PHONE NUMBER</span>}
                      name="phone"
                      rules={[{ required: true, message: 'Please enter your phone number' }]}
                    >
                      <InputMask
                        mask="usphone"
                        className="bg-zinc-800 font-barlow border-zinc-700 text-white"
                        placeholder="Your phone number"
                      />
                    </Form.Item>

                    <Form.Item
                      label={<span className="font-android !font-normal text-white">SERVICE INTERESTED IN</span>}
                      name="subject"
                      rules={[{ required: true, message: 'Please select a service' }]}
                    >
                      <Select
                        className="w-full"
                        placeholder="Select a service"
                        rootClassName="ant-select-dark"
                      >
                        <Option value="Excavation">Excavation</Option>
                        <Option value="Site Preparation">Site Preparation</Option>
                        <Option value="Snow Removal">Snow Removal</Option>
                        <Option value="Demolition">Demolition</Option>
                        <Option value="Grading & Leveling">Grading & Leveling</Option>
                        <Option value="Trenching">Trenching</Option>
                        <Option value="Other">Other</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label={<span className="font-android !font-normal text-white">MESSAGE</span>}
                      name="message"
                      rules={[{ required: true, message: 'Please enter your message' }]}
                    >
                      <TextArea
                        rows={6}
                        className="bg-zinc-800 font-barlow text-white"
                        placeholder="Your message"
                      />
                    </Form.Item>
                    <PrimaryButton
                      type="primary"
                      htmlType="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                    </PrimaryButton>
                  </Form>
                ) : (
                  <ThankYouMessage />
                )}
              </div>
            </Col>

            {/* Map Column */}
            <Col xs={24} lg={12}>
              <Title level={2} className="font-android !font-normal text-yellow-principal">OUR LOCATION</Title>
              <div className="w-full bg-zinc-900 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3077.7558386183344!2d-120.18967822392834!3d39.32834757148071!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80995f23aaab6fd7%3A0x72b8c56e516a875f!2s11168%20W%20River%20St%2C%20Truckee%2C%20CA%2096161%2C%20EUA!5e0!3m2!1sen-US!2sen!4v1716224307563!5m2!1sen-US!2sen"
                  width="100%"
                  height="350"
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ruppert Inc. Location"
                  className="w-full h-[400px] mx-auto"
                ></iframe>
              </div>
              <Paragraph className="font-barlow text-gray-300 text-lg mt-4">
                We're conveniently located to serve the entire Truckee-Tahoe region. Feel free to visit our
                office during business hours to discuss your project in person.
              </Paragraph>
            </Col>
          </Row>
        </div>
      </section>

      {/* FAQ Section */}
      {/* <section className="w-full py-20 bg-black">
        <div className="container mx-auto px-4">
          <Title level={2} className="!text-white text-center pb-12 font-android">FREQUENTLY ASKED QUESTIONS</Title>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-zinc-900 p-6 rounded-lg">
              <Title level={4} className="!text-white mb-4 font-android">WHAT AREAS DO YOU SERVICE?</Title>
              <Paragraph className="text-gray-300" style={paragraphStyle}>
                We primarily service Truckee and the surrounding areas including North Lake Tahoe,
                Donner Lake, and Martis Valley. Contact us for specific service area questions.
              </Paragraph>
            </div>

            <div className="bg-zinc-900 p-6 rounded-lg">
              <Title level={4} className="!text-white mb-4 font-android">HOW DO I REQUEST A QUOTE?</Title>
              <Paragraph className="text-gray-300" style={paragraphStyle}>
                You can request a quote by filling out our contact form, calling our office,
                or using the "Get a Quote" button on our website. We'll get back to you within 1-2 business days.
              </Paragraph>
            </div>

            <div className="bg-zinc-900 p-6 rounded-lg">
              <Title level={4} className="!text-white mb-4 font-android">ARE YOU LICENSED AND INSURED?</Title>
              <Paragraph className="text-gray-300" style={paragraphStyle}>
                Yes, we are fully licensed and insured. Our California contractor's license
                number is available upon request, and we maintain comprehensive insurance coverage.
              </Paragraph>
            </div>

            <div className="bg-zinc-900 p-6 rounded-lg">
              <Title level={4} className="!text-white mb-4 font-android">WHEN DO YOU OFFER SNOW REMOVAL SERVICES?</Title>
              <Paragraph className="text-gray-300" style={paragraphStyle}>
                Our commercial snow removal services typically run from November through April,
                depending on weather conditions. We offer both seasonal contracts and one-time services.
              </Paragraph>
            </div>
          </div>
        </div>
      </section> */}
    </div>
  );
};
